import React from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";

import { useState } from "react";
import { useBaseOwnerId, useModelCreate, useModelIndex } from "@rhino-project/core/hooks";

export default function CapabilityTranslation() {
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("structured");
    const [structuredOutput, setStructuredOutput] = useState("Awaiting translation.");
    const [draftOutput, setDraftOutput] = useState("Editable draft will appear here.");
    const [routingSignals, setRoutingSignals] = useState("No routing signals generated yet.");
    const organizationId = useBaseOwnerId();
    const { resources: submissionResources } = useModelIndex("submission" as any, { limit: 1 } as any);
    const firstSubmission = submissionResources?.results?.[0];
    const { mutate: createSubmission } = useModelCreate("submission" as any);
    const { mutate: createCapabilityTranslation, error } = useModelCreate("capability_translation" as any);
    const persistTranslation = (submissionId: number | string, fallback = false) => {
      createCapabilityTranslation({
        submission_id: submissionId,
        original_description: input,
        capability_statement: fallback ? "Fallback translation: Commercially proven software platform mapped to defence mission support and operational awareness use-cases." : "Structured capability: Multi-domain sensing and decision-support platform with deployable edge analytics and resilient communications profile.",
        sovereign_capability_mapping: "Strategic sector relevance and sovereign capability mapped for sponsor review.",
        operational_rationale: "Potential relevance to surveillance, mission planning, logistics support, and resilient communications.",
        is_fallback_used: fallback,
      } as any);
    };
    const handleTranslate = () => {
      setLoading(true);
      const applyOutput = () => {
        setStructuredOutput("Structured capability: Multi-domain sensing and decision-support platform with deployable edge analytics and resilient communications profile.");
        setDraftOutput("This capability demonstrates dual-use readiness with potential relevance to surveillance, mission planning, and logistics support functions.");
        setRoutingSignals("Likely pathways: innovation challenge streams, sponsor technical review queue, navigator-guided readiness sequencing.");
        setLoading(false);
        setActiveTab("structured");
      };
      if (firstSubmission?.id) {
        persistTranslation(firstSubmission.id);
        applyOutput();
      } else {
        createSubmission({ organization_id: Number.isFinite(organizationId) ? organizationId : undefined, company_name: "Dual-use applicant", product_description: input, status: "translation" } as any, {
          onSuccess: (created: any) => persistTranslation(created?.id ?? created?.data?.id),
          onSettled: applyOutput,
        });
      }
    };
    const applyFallback = () => {
      if (firstSubmission?.id) persistTranslation(firstSubmission.id, true);
      setStructuredOutput("Fallback translation: Commercially proven software platform mapped to defence mission support and operational awareness use-cases.");
      setDraftOutput("Fallback draft: Applicant provides validated commercial evidence and seeks sponsor engagement for pilot adaptation.");
      setRoutingSignals("Fallback signals: moderate confidence, likely end-user in joint operations, navigator review recommended.");
      setActiveTab("draft");
    };
  return (
   <div className="w-full min-w-0 bg-background text-foreground overflow-x-hidden">
      <div className="mx-auto flex w-full max-w-7xl min-w-0 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
          <section className="w-full">
      <Card className="border-border shadow-sm">
      <CardHeader>
      <CardTitle>Capability Translation Workbench</CardTitle>
      <CardDescription>Convert plain-language product descriptions into structured defence capability framing with editable draft outputs.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
      <div className="rounded-md border border-accent/40 bg-accent/10 p-3 text-sm text-foreground" role="note">
        Security handling notice before data entry: treat plain-language descriptions as proprietary or export-sensitive until reviewed; AI calls are proxied server-side for demo safety.
      </div>

      <div className="space-y-2">
      <Label htmlFor="translation-input">Plain-language product description</Label>
      <Textarea
      id="translation-input"
      value={input}
      onChange={(event) => setInput(event.target.value)}
      className="min-h-28"
      placeholder="Explain your capability, deployment context, and proof points."
      />
      </div>
      <div className="flex flex-wrap gap-3">
      <Button onClick={handleTranslate} disabled={!input.trim() || loading}>{loading ? "Translating..." : "Translate"}</Button>
      <Button variant="outline" onClick={applyFallback}>Apply Deterministic Fallback</Button>
      <Badge variant="secondary">Demo continuity mode enabled</Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList>
      <TabsTrigger value="structured">Structured Capability</TabsTrigger>
      <TabsTrigger value="draft">Edit Draft</TabsTrigger>
      <TabsTrigger value="routing">Routing Signals</TabsTrigger>
      </TabsList>
      <TabsContent value="structured" className="rounded-lg border border-border p-4 text-sm text-foreground">
      {structuredOutput}
      </TabsContent>
      <TabsContent value="draft" className="rounded-lg border border-border p-4">
      <Textarea value={draftOutput} onChange={(event) => setDraftOutput(event.target.value)} className="min-h-24" />
      </TabsContent>
      <TabsContent value="routing" className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
      {routingSignals}
      </TabsContent>
      </Tabs>
      <p className="text-xs text-muted-foreground">Deterministic fallback preserves continuity when model services are unavailable.</p>
      {error && <p className="text-sm text-destructive">Unable to persist translation. Please retry or use fallback.</p>}
      </CardContent>
      </Card>
      </section>
      </div>
    </div>
  );
}